/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import * as React from 'react';

import { IActionContext } from '@msdyn365-commerce/core';
import { SimpleProduct } from '@msdyn365-commerce/retail-proxy';
import { Button, Modal, ModalBody, ModalHeader } from '@msdyn365-commerce-modules/utilities';

import axios from 'axios';
import { encodeKlaviyoItemId } from '../../../../common/encodeKlaviyoItemId';
import { SubscriptionConfirmationModal } from './subscription-confirmation-modal';
import { subscribeToList } from '../../../hei-klaviyo-signup-form/subscribeToList';

export interface BackInStockSubscriptionModalProps {
    className: string;
    modalOpen: boolean | undefined;
    setModalOpen(newValue: boolean): void;
    context: IActionContext;
    product: SimpleProduct;
}

export const BackInStockSubscriptionModal = (popupProps: BackInStockSubscriptionModalProps) => {
    const { className, modalOpen, setModalOpen, context, product } = popupProps;
    const [isUpdating, setIsUpdating] = React.useState(false);
    const [isSubmitted, setIsSubmitted] = React.useState(false);
    const [email, setEmail] = React.useState(context.requestContext.user.emailAddress ?? '');
    const [subscribeToNewsletter, setSubscribeToNewsletter] = React.useState(false);
    const [errMsg, setErrMsg] = React.useState('');
    const closeModal = React.useCallback(() => {
        setModalOpen(false);
    }, [setModalOpen]);

    const handleSubscription = () => {
        if (!email) {
            setErrMsg('Please enter your email address.');
            // eslint-disable-next-line security/detect-unsafe-regex
        } else if (!/^[a-zA-Z0-9._-]+(\+[a-zA-Z0-9._-]+)?@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(email)) {
            setErrMsg('Please enter a valid email address.');
        } else {
            setErrMsg('');

            setIsUpdating(true);
            const publicKey = context.requestContext.app.config.heiKlaviyoPublicKey;
            if (publicKey) {
                const endpoint = `https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=${publicKey}`;
                const apiRev = context.requestContext.app.config.heiKlaviyoApiRevision ?? '2024-05-15';
                void axios
                    .post(
                        endpoint,
                        {
                            data: {
                                type: 'back-in-stock-subscription',
                                attributes: {
                                    channels: ['EMAIL'],
                                    profile: {
                                        data: {
                                            type: 'profile',
                                            attributes: {
                                                email: email,
                                                id: context.requestContext.user.customerAccountNumber || undefined
                                            }
                                        }
                                    }
                                },
                                relationships: {
                                    variant: {
                                        data: {
                                            type: 'catalog-variant',
                                            id: encodeKlaviyoItemId(product.ProductNumber ?? product.ItemId ?? '')
                                        }
                                    }
                                }
                            }
                        },
                        {
                            headers: {
                                revision: apiRev
                            }
                        }
                    )
                    .then(() => {
                        setIsUpdating(false);
                        closeModal();
                        setIsSubmitted(true);
                    })
                    .catch(err => {
                        process.env.NODE_ENV === 'development' && console.log('KlaviyoSubscription', err);
                        setErrMsg('An error has occurred, please try again later.');
                        setIsUpdating(false);
                    });

                if (subscribeToNewsletter) {
                    void subscribeToList(context.requestContext, { email });
                }
            }
        }
    };

    if (isSubmitted) {
        return (
            <SubscriptionConfirmationModal
                modalOpen={isSubmitted}
                setModalOpen={isOpen => {
                    setIsSubmitted(isOpen);
                }}
                className={className}
            />
        );
    }

    return (
        <Modal isOpen={modalOpen} onClosed={closeModal} className={`${className}__dialog`} toggle={closeModal}>
            <ModalHeader toggle={closeModal} className={`${className}__dialog__header`}>
                <span>{'Thank you for your interest in this item'}</span>
            </ModalHeader>
            <ModalBody className={`${className}__dialog__body`}>
                <p>{'Enter your email address and we will notify you when it becomes available.'}</p>
                <p>
                    {
                        "An email notification guarantees neither inventory nor price. But don't worry: should you miss out on the first shipment, you can always request another notification."
                    }
                </p>
                <div className='form-group'>
                    <input
                        type='email'
                        className='form-control'
                        placeholder='Email'
                        value={email}
                        onChange={evt => setEmail(evt.currentTarget.value)}
                        disabled={isUpdating}
                    />
                    <Button disabled={isUpdating} className={'btn'} onClick={handleSubscription}>
                        {'Submit'}
                    </Button>
                </div>
                {errMsg && <div className='invalid-feedback'>{errMsg}</div>}
                <div className='form-check'>
                    <label className='form-check-label' htmlFor='chk-klaviyo-back-in-stock-subscription-modal'>
                        <input
                            className='form-check-input'
                            type='checkbox'
                            checked={subscribeToNewsletter}
                            id='chk-klaviyo-back-in-stock-subscription-modal'
                            onChange={evt => setSubscribeToNewsletter(evt.target.checked)}
                        />
                        Also sign up to Henry&apos;s emails! Learn about events near you, great deals, and new products. Plus, get tips ot
                        improve your photography and creator skills!
                    </label>
                </div>
            </ModalBody>
        </Modal>
    );
};
